import { Box } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { ZodObject } from 'zod'
import { FormFieldType } from 'src/enums'
import { FormField, getFieldKey } from 'src/generated-types/form-field'
import { ICustomerFormSchema, IFormSchemaReportDeviationDto } from 'src/generated-types/form-schema'
import { IDeviationType } from 'src/generated-types/deviation-type'
import { useZodForm } from 'src/hooks/zod-form'
import { ChecklistField } from 'src/components/formGenerator/ChecklistField'
import FieldSelector from 'src/components/formGenerator/FieldSelector'
import { ChecklistDeviationMenu, DeviationMenuState } from 'src/components/checklists/ChecklistDeviationMenu'
import { ChecklistFieldsLayout } from 'src/components/checklists/ChecklistFieldsLayout'
import { buildSchemaAndInitialValues } from 'src/components/checklists/checklist-utils'
import { useDialogCtx } from 'src/hooks/context-hooks'
import CreateDeviationDialog from 'src/screens/localities/deviations/CreateDeviation'

export interface ISingleChecklistFormProps {
  schema: ICustomerFormSchema
  onChangeValues: (
    schemaId: number,
    versionId: number,
    values: Record<string, any>,
    isValid: boolean,
    formSchemaReportDeviations?: IFormSchemaReportDeviationDto[],
  ) => void
  customerId: EntityId
  mainComponentId?: EntityId
  savedValues?: Record<string, any>
  disabledAddDeviation?: boolean
  readOnly?: boolean
  collectDeviationsWithoutSaving?: boolean
}

export const SingleChecklistForm: React.FC<ISingleChecklistFormProps> = ({
  schema,
  onChangeValues,
  customerId,
  mainComponentId,
  savedValues,
  readOnly = false,
  disabledAddDeviation = false,
  collectDeviationsWithoutSaving = false,
}) => {
  const { zodSchema, initialValues, layouts } = buildSchemaAndInitialValues(schema.version?.layout, savedValues)
  const form = useZodForm(zodSchema as ZodObject<any>, { defaultValues: initialValues })
  const { showDialog } = useDialogCtx()
  const [deviationMenuAnchor, setDeviationMenuAnchor] = useState<DeviationMenuState | null>(null)
  const [formSchemaReportDeviations, setFormSchemaReportDeviations] = useState<IFormSchemaReportDeviationDto[]>([])

  // Notify parent of changes including form schema report deviations
  const notifyChange = useCallback(
    (values: Record<string, any>) => {
      const isValid = (zodSchema as ZodObject<any>).safeParse(values).success
      onChangeValues(
        schema.id!,
        schema.version?.id ?? 0,
        values as Record<string, any>,
        isValid,
        collectDeviationsWithoutSaving ? formSchemaReportDeviations : undefined,
      )
    },
    [
      zodSchema,
      schema.id,
      schema.version?.id,
      onChangeValues,
      collectDeviationsWithoutSaving,
      formSchemaReportDeviations,
    ],
  )

  useEffect(() => {
    const subscription = form.watch(values => {
      notifyChange(values as Record<string, any>)
    })
    return () => subscription.unsubscribe()
  }, [form, notifyChange])

  // Also notify when form schema report deviations change
  useEffect(() => {
    if (collectDeviationsWithoutSaving && formSchemaReportDeviations.length >= 0) {
      notifyChange(form.getValues())
    }
  }, [formSchemaReportDeviations, collectDeviationsWithoutSaving])

  const handleDeviationCreated = useCallback((deviationDto: IFormSchemaReportDeviationDto) => {
    setFormSchemaReportDeviations(prev => [...prev, deviationDto])
  }, [])

  const handleRemoveInlineDeviation = useCallback((tempId: string) => {
    setFormSchemaReportDeviations(prev => prev.filter(d => d.deviation.tempId !== tempId))
    setDeviationMenuAnchor(null)
  }, [])

  const getItemDeviations = (fieldId: string, itemId: string): IFormSchemaReportDeviationDto[] => {
    if (collectDeviationsWithoutSaving) {
      return formSchemaReportDeviations.filter(d => d.checklistFieldId === fieldId && d.checklistItemId === itemId)
    }
    return []
  }

  const getItemInlineDeviations = (fieldId: string, itemId: string): IFormSchemaReportDeviationDto[] => {
    return formSchemaReportDeviations.filter(d => d.checklistFieldId === fieldId && d.checklistItemId === itemId)
  }

  const handleOpenDeviationMenu = (event: React.MouseEvent<HTMLElement>, fieldId: string, itemId: string) => {
    event.stopPropagation()
    setDeviationMenuAnchor({ element: event.currentTarget, itemId, fieldId })
  }

  const handleOpenAddDeviationModal = (event: React.MouseEvent<HTMLElement>, fieldId: string, itemId: string) => {
    event.stopPropagation()
    showDialog(
      CreateDeviationDialog,
      {
        componentProps: {
          customerId,
          deviationType: IDeviationType.MainComponent,
          deviationTypeFromParent: IDeviationType.MainComponent,
          mainComponentId,
          checklistFieldMetadata: {
            fieldId,
            itemId,
          },
          returnWithoutSaving: collectDeviationsWithoutSaving,
          onDeviationCreated: collectDeviationsWithoutSaving ? handleDeviationCreated : undefined,
          isDisableWizard: true,
        },
        opts: { maxWidth: 'lg' },
        isAutoCloseDisabled: true,
      },
      undefined,
      true,
    )
  }

  const handleCloseDeviationMenu = () => {
    setDeviationMenuAnchor(null)
  }

  const handleFieldChange = (fieldKey: string, value: any) => {
    form.setValue(fieldKey as any, value, { shouldValidate: true })
  }

  const renderField = (field: FormField, layoutIndex: number, fieldIndex: number) => {
    const fieldKey = getFieldKey(field)
    const isChecklistField = field.type === FormFieldType.checklist

    if (isChecklistField) {
      return (
        <ChecklistField
          field={field}
          disabled={readOnly}
          disabledAddDeviation={disabledAddDeviation}
          form={form as any}
          onChange={value => handleFieldChange(fieldKey, value)}
          onDragStart={() => {}}
          setSelectedSettingField={() => {}}
          getItemDeviations={getItemDeviations}
          handleOpenDeviationMenu={handleOpenDeviationMenu}
          handleOpenAddDeviationModal={handleOpenAddDeviationModal}
          customerId={customerId}
        />
      )
    }

    return (
      <FieldSelector
        field={field}
        layoutIndex={layoutIndex}
        index={fieldIndex}
        setSelectedSettingField={() => {}}
        disabled={readOnly}
        fieldDisabled={false}
        onChange={value => handleFieldChange(fieldKey, value)}
        form={form as any}
      />
    )
  }

  return (
    <>
      <Box>
        <ChecklistFieldsLayout layout={layouts} renderField={renderField} />
      </Box>
      <ChecklistDeviationMenu
        anchor={deviationMenuAnchor}
        onClose={handleCloseDeviationMenu}
        pendingDeviations={
          collectDeviationsWithoutSaving && deviationMenuAnchor
            ? getItemInlineDeviations(deviationMenuAnchor.fieldId, deviationMenuAnchor.itemId)
            : []
        }
        onRemovePendingDeviation={handleRemoveInlineDeviation}
      />
    </>
  )
}
